import { useEffect, useState } from "react";
import type { InstagramPost } from "@shared/types/foyer";

/** Refresh the feed every 10 minutes - the server caches for the same window,
 *  so a TV that runs for weeks keeps its grid current without hammering us. */
const REFRESH_MS = 10 * 60 * 1000;

export interface InstagramMediaState {
  posts: InstagramPost[];
  loading: boolean;
  error: boolean;
}

/**
 * Fetch a premise's recent Instagram posts for the TV feed grid. Disabled
 * fetches (no premiseId, or `enabled === false`) resolve to an empty list so
 * the caller can fall back to the invite card. Polls on an interval so a
 * long-lived TV stays fresh.
 */
export function useInstagramMedia(
  premiseId: string | undefined,
  count: number,
  enabled: boolean
): InstagramMediaState {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled || !premiseId) {
      setPosts([]);
      setLoading(false);
      setError(false);
      return;
    }
    let alive = true;
    const load = (showSpinner: boolean) => {
      if (showSpinner) setLoading(true);
      setError(false);
      fetch(
        `/api/frontlayer/v1/instagram/media?premiseId=${encodeURIComponent(
          premiseId
        )}&limit=${count}`
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((j: { posts?: InstagramPost[] }) => {
          if (alive) setPosts(j.posts ?? []);
        })
        .catch(() => alive && setError(true))
        .finally(() => alive && setLoading(false));
    };
    load(true);
    const id = setInterval(() => load(false), REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [premiseId, count, enabled]);

  return { posts, loading, error };
}
